import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { collection, limit, orderBy, query } from 'firebase/firestore';
import { db } from '../../firebase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useFirestoreQuery } from '../../hooks/useFirestoreQuery';

/**
 * @param {import('firebase/firestore').Timestamp | Date | string | null | undefined} value
 * @returns {string}
 */
function formatDeliveryTimestamp(value) {
  if (!value) return '—';
  const date =
    typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Read-only recent delivery attempts for a single outbound webhook.
 * @param {{ webhookId: string }} props
 */
export default function WebhookDeliveryLog({ webhookId }) {
  const { companyId } = useWorkspace();

  const deliveriesQuery = useMemo(() => {
    if (!companyId || !webhookId) return null;
    return query(
      collection(
        db,
        'companies',
        companyId,
        'outbound_webhooks',
        webhookId,
        'deliveries',
      ),
      orderBy('deliveredAt', 'desc'),
      limit(5),
    );
  }, [companyId, webhookId]);

  const { data: deliveries, loading, error } = useFirestoreQuery(deliveriesQuery, {
    enabled: Boolean(companyId && webhookId),
  });

  if (!webhookId || !companyId) {
    return null;
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
        <CircularProgress size={14} />
        <Typography variant="caption" color="text.secondary">
          Loading delivery history…
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="caption" color="error">
        Could not load delivery history.
      </Typography>
    );
  }

  if (deliveries.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary" fontStyle="italic">
        No deliveries yet. History appears after ticket events are dispatched.
      </Typography>
    );
  }

  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, mb: 0.5, display: 'block' }}>
        Recent deliveries (last 5)
      </Typography>
      <List dense disablePadding>
        {deliveries.map((row) => {
          const success = row.success === true;
          const detail = success
            ? `HTTP ${row.httpStatusCode ?? '—'}`
            : row.errorMessage || `HTTP ${row.httpStatusCode ?? '—'}`;

          return (
            <ListItem key={row.id} disableGutters sx={{ py: 0.35, alignItems: 'flex-start' }}>
              <ListItemText
                primary={(
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      size="small"
                      icon={success ? <CheckCircleOutlineIcon /> : <ErrorOutlineIcon />}
                      label={success ? 'Success' : 'Failed'}
                      color={success ? 'success' : 'error'}
                      variant="outlined"
                      sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: '0.7rem' } }}
                    />
                    {row.eventName ? (
                      <Typography variant="caption" color="text.secondary">
                        {row.eventName}
                      </Typography>
                    ) : null}
                    <Typography variant="caption" color="text.secondary">
                      {formatDeliveryTimestamp(row.deliveredAt)}
                    </Typography>
                  </Box>
                )}
                secondary={(
                  <Typography
                    variant="caption"
                    color={success ? 'text.secondary' : 'error'}
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {detail}
                  </Typography>
                )}
              />
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
}
